/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  CommunityRestService,
  EmailCampaignRestService,
  MasterZoneRestService,
  OfficeRestService,
  UserRestService,
} from '@gh/builder-shared-data';
import {
  createDialogRoute,
  Dictionary,
  DictionaryService,
  InitDialogViewService,
} from '@gh/common-shared';
import { getAutocompleteItemId } from '@gh/common-shared-data';
import { observableBasedSlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { includesIgnoreCase } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  CommunityFilter,
  CommunityFinderListItem,
  DictionaryFinderDialogConfig,
  GenericFinderDialogComponent,
  MasterZoneFinderListItem,
  OfficeFilter,
  OfficeFinderListItem,
  SalesPersonFilter,
  SalesPersonFinderListItem,
} from '..';
import { EmailCampaignTemplate, GenericFinderDialogConfig } from './generic-finder.model';

@Injectable()
export class EmailCampaignTemplateFinderBusinessService implements InitDialogViewService<GenericFinderDialogConfig> {
  constructor(private translations: TranslationService,
              private emailCampaignRest: EmailCampaignRestService) {
  }

  initDialogView(route: DialogRouteSnapshot<GenericFinderDialogConfig>): any {
    return {
      getId: (item: EmailCampaignTemplate) => item.id,
      defaultTitle: this.translations.get('lbl_select_email_campaign_template'),
      dataSourceFactory: (filter: string) =>
        observableBasedSlicedDataSource(this.emailCampaignRest.findAllTemplates().pipe(
          map$((templates: EmailCampaignTemplate[]) =>
            templates.filter((template) => includesIgnoreCase(template.name, filter))))),
      columnDefs: [
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 250 },
      ],
    };
  }
}

@Injectable()
export class MasterZoneFinderBusinessService implements InitDialogViewService<GenericFinderDialogConfig> {
  constructor(private translations: TranslationService,
              private masterZoneRest: MasterZoneRestService) {
  }

  initDialogView(route: DialogRouteSnapshot<GenericFinderDialogConfig>): any {
    return {
      getId: (item: MasterZoneFinderListItem) => item.id,
      defaultTitle: this.translations.get('lbl_select_master_zones'),
      dataSourceFactory: (filter: string) =>
        observableBasedSlicedDataSource(this.masterZoneRest.findAllActive().pipe(
          map$((masterZones: MasterZoneFinderListItem[]) =>
            masterZones.filter((masterZone) => includesIgnoreCase(masterZone.name, filter))))),
      columnDefs: [
        { field: 'name', headerName: this.translations.get('lbl_master_zone_name'), width: 250 },
      ],
    };
  }
}

@Injectable()
export class OfficeFinderBusinessService implements InitDialogViewService<GenericFinderDialogConfig> {
  constructor(private translations: TranslationService,
              private officeRest: OfficeRestService) {
  }

  initDialogView(route: DialogRouteSnapshot<GenericFinderDialogConfig>): any {
    return {
      getId: (item: OfficeFinderListItem) => item.officeId,
      defaultTitle: this.translations.get('lbl_select_offices'),
      dataSourceFactory: (name: string, filter: OfficeFilter) =>
        this.officeRest.findAllForFinderAsDataSource({ ...filter, name }),
      columnDefs: [
        { field: 'name', headerName: this.translations.get('lbl_office_name'), width: 250 },
      ],
    };
  }
}

@Injectable()
export class CommunityFinderBusinessService implements InitDialogViewService<GenericFinderDialogConfig> {
  constructor(private translations: TranslationService,
              private communityRest: CommunityRestService) {
  }

  initDialogView(route: DialogRouteSnapshot<GenericFinderDialogConfig>): any {
    return {
      getId: (item: CommunityFinderListItem) => item.communityId,
      defaultTitle: this.translations.get('lbl_select_communities'),
      dataSourceFactory: (searchString: string, filter: CommunityFilter) =>
        this.communityRest.findAllForFinderAsDataSource({ ...filter, searchString }),
      columnDefs: [
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 250 },
      ],
    };
  }
}

@Injectable()
export class SalesPersonFinderBusinessService implements InitDialogViewService<GenericFinderDialogConfig> {
  constructor(private translations: TranslationService,
              private userRest: UserRestService) {
  }

  initDialogView(route: DialogRouteSnapshot<GenericFinderDialogConfig>): any {
    return {
      getId: (item: SalesPersonFinderListItem) => item.id,
      defaultTitle: this.translations.get('lbl_select_salespersons'),
      dataSourceFactory: (searchString: string, filter: SalesPersonFilter) =>
        this.userRest.findAllSalesPersonsAsDataSource({ ...filter, searchString }),
      columnDefs: [
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 250 },
      ],
    };
  }
}

@Injectable()
export class DictionaryFinderBusinessService implements InitDialogViewService<DictionaryFinderDialogConfig> {
  constructor(private translations: TranslationService,
              private dictionaries: DictionaryService) {
  }

  initDialogView(route: DialogRouteSnapshot<DictionaryFinderDialogConfig>): any {
    return {
      getId: getAutocompleteItemId,
      defaultTitle: route.input.title,
      dataSourceFactory: (filter: string) => {
        const dictionary$: Observable<Dictionary<any>> = route.input.dictionaryName
          ? this.dictionaries.get(route.input.dictionaryName)
          : this.dictionaries.getByEnum(route.input.dictionaryEnum!);

        return observableBasedSlicedDataSource(dictionary$.pipe(map$((dictionary) =>
          dictionary.seq().filter((item) => includesIgnoreCase(item.name, filter)))));
      },
      columnDefs: [
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 250 },
      ],
    };
  }
}

export const emailCampaignTemplateFinderDialogRoute = createDialogRoute({
  component: GenericFinderDialogComponent,
  init: EmailCampaignTemplateFinderBusinessService,
});

export const masterZoneFinderDialogRoute = createDialogRoute({
  component: GenericFinderDialogComponent,
  init: MasterZoneFinderBusinessService,
});

export const officeFinderDialogRoute = createDialogRoute({
  component: GenericFinderDialogComponent,
  init: OfficeFinderBusinessService,
});

export const communityFinderDialogRoute = createDialogRoute({
  component: GenericFinderDialogComponent,
  init: CommunityFinderBusinessService,
});

export const salesPersonFinderDialogRoute = createDialogRoute({
  component: GenericFinderDialogComponent,
  init: SalesPersonFinderBusinessService,
});

export const dictionaryFinderDialogRoute = createDialogRoute({
  component: <any>GenericFinderDialogComponent,
  init: DictionaryFinderBusinessService,
});
